/**
 * Unified layout component catalog helpers.
 * Built-ins (Rich Text, Recent Posts) and widgets share one picker catalog.
 */
import {
  collectSections,
  countType,
  nextWidgetId,
  MAX_SECTIONS,
  MAX_WIDGET_SECTIONS
} from './layout-split.js';

export const RICH_TEXT_CATALOG_KEY = 'richtext';
export const STOCK_RICH_TEXT_WIDGET_NAMES = ['Rich Text', 'About Me'];

/**
 * @typedef {Object} LayoutComponentCatalogEntry
 * @property {string} key
 * @property {string} name
 * @property {string} [icon]
 * @property {string} [description]
 * @property {number} [maxCount]
 * @property {string} sectionType
 * @property {number} [widgetId]
 * @property {'builtin'|'widget'} kind
 * @property {string} [status]
 */

export const PROFILE_BUILTIN_COMPONENTS = [
  {
    key: 'posts',
    name: 'Recent Posts',
    icon: '📰',
    description: 'Your latest posts for friends to browse.',
    maxCount: 1,
    sectionType: 'posts',
    kind: 'builtin'
  }
];

/**
 * @param {object[]} widgets
 * @returns {object|undefined}
 */
export function findStockRichTextWidget(widgets = []) {
  return widgets.find((w) => STOCK_RICH_TEXT_WIDGET_NAMES.includes(w.name));
}

/**
 * @param {object} widget
 * @returns {LayoutComponentCatalogEntry}
 */
export function richTextCatalogEntry(widget) {
  const id = parseInt(widget.id, 10);
  return {
    key: RICH_TEXT_CATALOG_KEY,
    name: 'Rich Text',
    icon: '📝',
    description: widget.description || 'Formatted text with markdown — links, lists, bold, and more.',
    maxCount: 1,
    sectionType: 'widget',
    widgetId: id,
    kind: 'builtin',
    status: 'approved',
    configSchema: widget.configSchema,
    requiresOAuth: false,
    oauthConfig: null,
    oauthClientId: null,
    hasOAuthClientSecret: false
  };
}

/**
 * @param {object} widget
 * @returns {LayoutComponentCatalogEntry}
 */
export function widgetToCatalogEntry(widget, catalogGroup = 'widget') {
  const id = parseInt(widget.id, 10);
  const group = widget.catalogGroup || catalogGroup;
  return {
    key: `widget-${id}`,
    name: widget.name,
    icon: '🧩',
    description: widget.description || null,
    maxCount: MAX_WIDGET_SECTIONS,
    sectionType: 'widget',
    widgetId: id,
    kind: group === 'community' && widget.isPlatform ? 'builtin' : 'widget',
    catalogGroup: group,
    status: widget.status || 'active',
    systemCatalogStatus: widget.systemCatalogStatus || widget.system_catalog_status || 'none',
    configSchema: widget.configSchema,
    requiresOAuth: widget.requiresOAuth,
    oauthConfig: widget.oauthConfig,
    oauthClientId: widget.oauthClientId || null,
    hasOAuthClientSecret: widget.hasOAuthClientSecret === true,
    isPlatform: Boolean(widget.isPlatform)
  };
}

/**
 * @param {object[]|{ myWidgets?: object[], communityWidgets?: object[], widgets?: object[] }} catalogInput
 * @param {LayoutComponentCatalogEntry[]} [builtins]
 * @returns {LayoutComponentCatalogEntry[]}
 */
export function buildProfileComponentCatalog(catalogInput = [], builtins = PROFILE_BUILTIN_COMPONENTS) {
  const catalog = [];
  let myWidgets = [];
  let communityWidgets = [];

  if (catalogInput && !Array.isArray(catalogInput)) {
    myWidgets = catalogInput.myWidgets || [];
    communityWidgets = catalogInput.communityWidgets || catalogInput.widgets || [];
  } else if (Array.isArray(catalogInput)) {
    communityWidgets = catalogInput;
  }

  const stockRichText = findStockRichTextWidget(communityWidgets)
    || findStockRichTextWidget(myWidgets);
  if (stockRichText) {
    catalog.push({
      ...richTextCatalogEntry(stockRichText),
      catalogGroup: 'community'
    });
  }

  catalog.push(...builtins.map((entry) => ({ ...entry, catalogGroup: 'community' })));

  for (const widget of myWidgets) {
    if (!widget?.id) continue;
    if (stockRichText && parseInt(widget.id, 10) === parseInt(stockRichText.id, 10)) continue;
    catalog.push(widgetToCatalogEntry(widget, 'my'));
  }

  for (const widget of communityWidgets) {
    if (!widget?.id) continue;
    if (stockRichText && parseInt(widget.id, 10) === parseInt(stockRichText.id, 10)) continue;
    if (myWidgets.some((m) => parseInt(m.id, 10) === parseInt(widget.id, 10))) continue;
    catalog.push(widgetToCatalogEntry(widget, 'community'));
  }

  return catalog;
}

/**
 * @param {object} section
 * @param {number|null} [stockRichTextWidgetId]
 * @returns {string|null}
 */
export function sectionToCatalogKey(section, stockRichTextWidgetId = null) {
  if (!section) return null;
  if (section.type === 'posts') return 'posts';
  if (section.type === 'widget' && section.widgetId) {
    const wid = parseInt(section.widgetId, 10);
    if (stockRichTextWidgetId && wid === stockRichTextWidgetId) {
      return RICH_TEXT_CATALOG_KEY;
    }
    if (section.id === 'richtext' || section.id === 'about') {
      return RICH_TEXT_CATALOG_KEY;
    }
    return `widget-${section.widgetId}`;
  }
  return null;
}

/**
 * @param {LayoutComponentCatalogEntry} entry
 * @param {object} layoutRoot
 * @param {object|null} [currentSection]
 * @returns {{ id: string, type: string, widgetId?: number }}
 */
export function catalogEntryToSection(entry, layoutRoot, currentSection = null) {
  if (entry.sectionType === 'posts') {
    return { id: 'posts', type: 'posts' };
  }
  if (entry.sectionType === 'widget') {
    const widgetId = parseInt(entry.widgetId, 10);
    if (Number.isNaN(widgetId) || widgetId < 1) {
      throw new Error('Widget catalog entry requires a valid widgetId');
    }
    let id;
    if (entry.key === RICH_TEXT_CATALOG_KEY) {
      id = (currentSection?.id === 'about' || currentSection?.id === 'richtext')
        ? currentSection.id
        : 'richtext';
    } else {
      id = currentSection?.type === 'widget' ? currentSection.id : nextWidgetId(layoutRoot);
    }
    return { id, type: 'widget', widgetId };
  }
  throw new Error(`Unknown catalog sectionType: ${entry.sectionType}`);
}

/**
 * @param {LayoutComponentCatalogEntry} entry
 * @param {object} layoutRoot
 * @param {{ excludeSectionId?: string|null, mode?: 'add'|'change' }} [options]
 * @returns {boolean}
 */
export function canAddCatalogEntry(entry, layoutRoot, options = {}) {
  const { excludeSectionId = null, mode = 'add' } = options;
  const sections = collectSections(layoutRoot);

  if (mode === 'add' && sections.length >= MAX_SECTIONS) {
    return false;
  }

  if (entry.sectionType === 'widget') {
    let widgetCount = countType(layoutRoot, 'widget');
    if (excludeSectionId) {
      const excluded = sections.find((s) => s.id === excludeSectionId);
      if (excluded?.type === 'widget') {
        widgetCount -= 1;
      }
    }
    if (mode === 'add' && widgetCount >= MAX_WIDGET_SECTIONS) {
      return false;
    }
    if (mode === 'change' && widgetCount >= MAX_WIDGET_SECTIONS) {
      const excluded = sections.find((s) => s.id === excludeSectionId);
      if (!(excluded?.type === 'widget')) {
        return false;
      }
    }
    return true;
  }

  const max = entry.maxCount ?? 1;
  let typeCount = countType(layoutRoot, entry.sectionType);
  if (excludeSectionId) {
    const excluded = sections.find((s) => s.id === excludeSectionId);
    if (excluded?.type === entry.sectionType) {
      typeCount -= 1;
    }
  }
  return typeCount < max;
}

/**
 * @param {LayoutComponentCatalogEntry} entry
 * @returns {string}
 */
export function catalogEntryDisplayName(entry) {
  if (entry.systemCatalogStatus && entry.systemCatalogStatus !== 'none' && entry.systemCatalogStatus !== 'approved') {
    return `${entry.name} (${entry.systemCatalogStatus})`;
  }
  return entry.name;
}
